import { Component, OnInit, Injector, OnDestroy } from '@angular/core';
import { ProcedureService } from 'app/core/http/services/procedure.service';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { OrganizationLocationService } from 'app/core/http/services/organization-location.service';
import { Healthcareservice } from 'app/core/interfaces/healthcareservice';
import { HttpParams } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { OrganizationService } from 'app/modules/organization/organization.service';
import { Address } from 'app/core/interfaces/adress';
import { HealthcareserviceSpecialists } from 'app/core/interfaces/healthcareservice';
import { telecom } from 'app/core/models/Organization';
import { OrganizationInfoDialogComponent } from 'app/modules/shared/components/organization-info-dialog/organization-info-dialog.component';
import { ScheduleSelectPatientComponent } from 'app/modules/shared/components/schedule-select-patient/schedule-select-patient.component';
import { ScheduleOrganizationCardComponent } from 'app/modules/shared/components/schedule-organization-card/schedule-organization-card.component';
import { ScheduleSpecialistByOrganizationComponent } from 'app/modules/shared/pages/schedule-specialist-by-organization/schedule-specialist-by-organization.component';
import {
  addScheduleSearchSuggestion,
  friendlyProcedureName,
  groupScheduleSearchSuggestions,
  normalizeScheduleSearch,
  ScheduleSearchMode,
  ScheduleSearchSuggestion,
  ScheduleSearchSuggestionGroup,
} from 'app/modules/shared/models/schedule-search.models';
import { summarizeSchedulePayment } from 'app/modules/shared/models/schedule-payment-summary';

interface ProcedureCatalogGroup {
  key: string;
  name: string;
  services: Healthcareservice[];
  clinicCount: number;
}

interface ClinicCatalogGroup {
  key: string;
  organizationId: string;
  name: string;
  services: Healthcareservice[];
}

@Component({
  selector: 'page-schedule-procedure',
  templateUrl: './schedule-procedure.component.html',
  styleUrls: ['./schedule-procedure.component.scss'],
})
export class ScheduleSecretaryProcedureComponent extends BaseComponent implements OnInit, OnDestroy {
  public title = 'Agendar';
  public searchControl = new FormControl('');
  public procedures: Healthcareservice[] = [];
  public specialistResults: HealthcareserviceSpecialists[] = [];
  public proceduresHLFIR: any[] = [];
  public address: Address[] = [];
  public locations: any[] = [];
  public organizations: any[] = [];
  public secretaryLogged: any = {};
  public latLong: any[] = [];
  public nextLatLong: [number, number] = [0, 0];
  public currentPage: number = 0;
  public pageSize: number = 10;
  public autocompleteSuggestions: ScheduleSearchSuggestion[] = [];
  public showAutocompletePanel = false;
  public showAutocompleteHint = false;
  public activeSuggestionIndex = -1;
  public activeSearchMode: ScheduleSearchMode = 'procedure';
  public expandedGroupKey: string | null = null;

  distance: number = 200000;

  public get search(): string {
    return this.searchControl.value || '';
  }

  public hasSearched = false;

  public get showMessage(): boolean {
    if (this.loading || this.latLong.length == 0 || (this.procedures.length == 0 && this.latLong.length != 0)) {
      return true;
    }
    return false;
  }

  public get message(): string {
    if (this.loading) {
      return 'Buscando Exames mais proximos...';
    }
    if (this.latLong.length == 0) {
      return 'Ative sua localização para buscar os exames mais proximos';
    }
    if (this.procedures.length == 0 && this.latLong.length != 0) {
      return 'Não foi retornado nenhum exame para o raio definido...';
    }
    return '';
  }

  public get proceduresFilter(): any[] {
    const searchTerm = normalizeScheduleSearch(this.search);
    return this.procedures.filter((procedure) => {
      if (!searchTerm) return true;

      const providerNames = this.getSpecialistsForProcedure(procedure)
        .map((provider) => provider.practitioner_name)
        .join(' ');
      const searchableValues = [
        friendlyProcedureName(procedure.name),
        procedure.type,
        procedure.specialty,
        procedure.organization?.name,
        this.getCity(procedure.organization?.organization_id),
        providerNames,
      ];
      return searchableValues.some((value) => normalizeScheduleSearch(value).includes(searchTerm));
    });
  }

  public get procedureGroups(): ProcedureCatalogGroup[] {
    const groups = new Map<string, ProcedureCatalogGroup>();
    this.proceduresFilter.forEach((procedure) => {
      const name = friendlyProcedureName(procedure.name);
      const key = procedure.tuss_code ? `tuss:${procedure.tuss_code}` : `name:${normalizeScheduleSearch(name)}`;
      const group = groups.get(key) ?? { key, name, services: [], clinicCount: 0 };
      if (
        !group.services.some(
          (item) => item.healthcareservice_id === procedure.healthcareservice_id && this.getClinicKey(item) === this.getClinicKey(procedure),
        )
      ) {
        group.services.push(procedure);
      }
      groups.set(key, group);
    });
    return Array.from(groups.values()).map((group) => ({
      ...group,
      clinicCount: new Set(group.services.map((procedure) => this.getClinicKey(procedure))).size,
    }));
  }

  public get clinicGroups(): ClinicCatalogGroup[] {
    const groups = new Map<string, ClinicCatalogGroup>();
    this.proceduresFilter.forEach((procedure) => {
      const organizationId = procedure.organization.organization_id;
      const key = this.getClinicKey(procedure);
      const group: ClinicCatalogGroup = groups.get(key) ?? {
        key,
        organizationId,
        name: procedure.organization.name,
        services: [] as Healthcareservice[],
      };
      if (!group.services.some((item) => item.healthcareservice_id === procedure.healthcareservice_id)) {
        group.services.push(procedure);
      }
      groups.set(key, group);
    });
    return Array.from(groups.values());
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  public get paginatedProcedures(): any[] {
    const start = this.currentPage * this.pageSize;
    const end = start + this.pageSize;
    const groups = this.activeSearchMode === 'clinic' ? this.clinicGroups : this.procedureGroups;
    return groups.slice(start, end);
  }

  public get totalPages(): number {
    const groups = this.activeSearchMode === 'clinic' ? this.clinicGroups : this.procedureGroups;
    return Math.ceil(groups.length / this.pageSize);
  }

  public get catalogGroupCount(): number {
    return this.activeSearchMode === 'clinic' ? this.clinicGroups.length : this.procedureGroups.length;
  }

  public get autocompleteSuggestionGroups(): ScheduleSearchSuggestionGroup[] {
    return groupScheduleSearchSuggestions(this.autocompleteSuggestions);
  }

  public get visibleAutocompleteSuggestions(): ScheduleSearchSuggestion[] {
    return this.autocompleteSuggestionGroups.reduce((suggestions, group) => suggestions.concat(group.suggestions), [] as ScheduleSearchSuggestion[]);
  }

  public get activeSuggestionId(): string | null {
    return this.activeSuggestionIndex >= 0 ? `schedule-search-option-${this.activeSuggestionIndex}` : null;
  }

  constructor(
    private organizationService: OrganizationService,
    private procedureService: ProcedureService,
    private locationService: OrganizationLocationService,
    private injector: Injector,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    const userLogged = this.getUserLogged();

    this.secretaryLogged = {
      secretary_id: userLogged.id,
      name: userLogged.name,
      email: userLogged?.email,
      birthDate: userLogged.birthDate,
    };
    this.activeSearchMode = this.router.url.includes('/agendar/clinicas') ? 'clinic' : 'procedure';
    this.searchControl.setValue(localStorage.getItem('searchControl') ?? '', { emitEvent: false });
    this.setupAutocomplete();

    this.getOrganizations();
    this.latLong.length === 0 ? this.getCurrentPosition() : this.getProceduresNearest();
    this.setPatientEncounterRaio();
    this.getLocations();
  }

  setupAutocomplete(): void {
    this.searchControl.valueChanges.pipe(debounceTime(200), distinctUntilChanged()).subscribe((searchTerm) => {
      this.updateAutocompleteSuggestions(searchTerm);
      this.currentPage = 0;
    });
  }

  updateAutocompleteSuggestions(searchTerm: string | null): void {
    const term = searchTerm ?? '';
    localStorage.setItem('searchControl', term.toLowerCase());
    const trimmedTerm = term.trim();
    this.activeSuggestionIndex = -1;

    if (trimmedTerm.length < 2) {
      this.autocompleteSuggestions = [];
      this.showAutocompletePanel = false;
      this.showAutocompleteHint = trimmedTerm.length > 0;
      return;
    }

    const suggestions: ScheduleSearchSuggestion[] = [];
    this.procedures.forEach((procedure) => {
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: friendlyProcedureName(procedure.name),
          category: 'Exames e procedimentos',
          mode: 'procedure',
        },
        trimmedTerm,
      );
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: procedure.organization?.name,
          category: 'Clínicas e locais',
          mode: 'clinic',
        },
        trimmedTerm,
      );
      const city = this.getCity(procedure.organization.organization_id);
      if (city) {
        addScheduleSearchSuggestion(
          suggestions,
          {
            label: city,
            category: 'Clínicas e locais',
            mode: 'clinic',
          },
          trimmedTerm,
        );
      }
    });

    this.specialistResults.forEach((result) =>
      addScheduleSearchSuggestion(
        suggestions,
        {
          label: result.practitioner_name,
          category: 'Especialistas',
          mode: 'specialist',
        },
        trimmedTerm,
      ),
    );

    this.autocompleteSuggestions = suggestions;
    this.showAutocompletePanel = true;
    this.showAutocompleteHint = false;
  }

  getSuggestionId(suggestion: ScheduleSearchSuggestion): string {
    return `schedule-search-option-${this.visibleAutocompleteSuggestions.indexOf(suggestion)}`;
  }

  isSuggestionActive(suggestion: ScheduleSearchSuggestion): boolean {
    return this.visibleAutocompleteSuggestions.indexOf(suggestion) === this.activeSuggestionIndex;
  }

  activateSuggestion(suggestion: ScheduleSearchSuggestion): void {
    this.activeSuggestionIndex = this.visibleAutocompleteSuggestions.indexOf(suggestion);
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.search.trim().length >= 2 && !this.showAutocompletePanel) {
        this.updateAutocompleteSuggestions(this.search);
      }

      const suggestions = this.visibleAutocompleteSuggestions;
      if (suggestions.length === 0) return;

      event.preventDefault();
      this.showAutocompletePanel = true;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeSuggestionIndex =
        this.activeSuggestionIndex < 0
          ? direction > 0
            ? 0
            : suggestions.length - 1
          : (this.activeSuggestionIndex + direction + suggestions.length) % suggestions.length;
      return;
    }

    if (event.key === 'Enter' && this.showAutocompletePanel) {
      event.preventDefault();
      const suggestion = this.visibleAutocompleteSuggestions[this.activeSuggestionIndex];
      if (suggestion) {
        this.selectSuggestion(suggestion);
      } else {
        this.showAutocompletePanel = false;
      }
      return;
    }

    if (event.key === 'Escape' && this.showAutocompletePanel) {
      event.preventDefault();
      this.showAutocompletePanel = false;
      this.activeSuggestionIndex = -1;
    }
  }

  selectSuggestion(suggestion: ScheduleSearchSuggestion): void {
    localStorage.setItem('searchControl', suggestion.label.toLowerCase());
    this.searchControl.setValue(suggestion.label, { emitEvent: false });
    this.showAutocompletePanel = false;
    this.activeSuggestionIndex = -1;
    this.navigateToMode(suggestion.mode);
  }

  onInputFocus(): void {
    if (this.search.trim().length >= 2) {
      this.updateAutocompleteSuggestions(this.search);
    } else {
      this.showAutocompleteHint = this.search.trim().length > 0;
    }
  }

  onInputBlur(): void {
    setTimeout(() => {
      this.showAutocompletePanel = false;
      this.activeSuggestionIndex = -1;
    }, 150);
  }

  navigateToMode(mode: ScheduleSearchMode): void {
    localStorage.setItem('searchControl', this.search.toLowerCase());
    this.activeSearchMode = mode;
    this.expandedGroupKey = null;
    this.currentPage = 0;
    const routes: Record<ScheduleSearchMode, string> = {
      procedure: '/secretarias-new/agendar/exame',
      specialist: '/secretarias-new/agendar/especialistas/exame',
      clinic: '/secretarias-new/agendar/clinicas',
    };
    this.router.navigate([routes[mode]]);
  }

  toggleGroup(key: string): void {
    this.expandedGroupKey = this.expandedGroupKey === key ? null : key;
  }

  getSpecialistsForProcedure(procedure: Healthcareservice): HealthcareserviceSpecialists[] {
    const uniqueProviders = new Map<string, HealthcareserviceSpecialists>();
    this.specialistResults
      .filter(
        (item) => item.healthcareservice_id === procedure.healthcareservice_id && item.organization_id === procedure.organization.organization_id,
      )
      .forEach((provider) => uniqueProviders.set(provider.practitioner_id, provider));
    return Array.from(uniqueProviders.values());
  }

  getSpecialistNamesForProcedure(procedure: Healthcareservice): string[] {
    return this.getSpecialistsForProcedure(procedure)
      .map((provider) => provider.practitioner_name)
      .filter(Boolean);
  }

  getClinicKey(procedure: Healthcareservice): string {
    const organizationId = procedure.organization.organization_id;
    if (procedure.location?.location_id) return `clinic:${organizationId}:${procedure.location.location_id}`;

    const serviceAddress = procedure.location?.address;
    const organizationAddress = this.getAddress(organizationId);
    const signature = [
      serviceAddress?.street || organizationAddress?.street,
      serviceAddress?.number || organizationAddress?.number,
      serviceAddress?.city || organizationAddress?.city,
      serviceAddress?.state || organizationAddress?.state,
    ]
      .map((value) => normalizeScheduleSearch(value))
      .filter(Boolean)
      .join('|');
    return signature ? `clinic:${organizationId}:${signature}` : `clinic:${organizationId}`;
  }

  getClinicDisplayName(procedure: Healthcareservice): string {
    const address = procedure.location?.address;
    const organizationAddress = this.getAddress(procedure.organization.organization_id);
    const city = address?.city || organizationAddress?.city;
    const state = address?.state || organizationAddress?.state;
    return city ? `${procedure.organization.name} - ${city}${state ? `, ${state}` : ''}` : procedure.organization.name;
  }

  getProcedureDisplayName(value: string): string {
    return friendlyProcedureName(value);
  }

  hasButton() {
    if (this.latLong.length == 0) {
      return 'col-xs-12 col-lg-8 margin';
    }
    return 'col-xs-12';
  }

  public getCurrentPosition() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: any) => {
          this.latLong = [position.coords.latitude, position.coords.longitude];

          this.getProceduresNearest();
          this.getLocations();
        },
        (error: any) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              console.error('User denied the request for Geolocation.');
              break;
            case error.POSITION_UNAVAILABLE:
              console.error('Location information is unavailable.');
              break;
            case error.TIMEOUT:
              console.error('The request to get user location timed out.');
              break;
            default:
              console.error('An unknown error occurred.');
              break;
          }
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
    }
  }

  openOrnigazationInfos(organization_id: string) {
    const organization = this.organizations.find((organization) => organization.organization_id === organization_id);
    const organizationLocation = this.locations.find((location) => location.organization.organization_id === organization_id);

    if (organization!.photo == '') {
      organization!.photo = this.defaultOrganizationPhoto;
    }

    const data = {
      param: {
        organization,
        organizationLocation,
      },
    };

    this.dialog.openComponentDialog(OrganizationInfoDialogComponent, data);
  }

  async openEncounterRegistrationForm(procedure: any) {
    await this.getOrganizationLocation(procedure);

    const organization = this.organizations.find((organization) => organization.organization_id == procedure?.organization.organization_id);

    const data = {
      title: 'Agendar consulta',
      param: {
        procedure: procedure,
        type: 'Exame',
        actor: 'SECRETARY',
        address: this.getAddressForProcedure(procedure),
        secretary: this.secretaryLogged,
        latLong: this.nextLatLong,
        distance: this.distance,
        position: this.latLong.join(','),
        organization: organization,
        organization_id: procedure?.organization.organization_id,
        healthcareservice_id: procedure?.healthcareservice_id,
        dealer: 'Organização',
      },
    };

    if (procedure?.serviceDealer == 'Organização') {
      this.dialog.openComponentDialog(ScheduleOrganizationCardComponent, data).subscribe((res: any) => {});
    } else {
      this.dialog.openComponentDialog(ScheduleSpecialistByOrganizationComponent, data).subscribe((res: any) => {});
    }
  }

  getOrganizationLocation(procedure: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const organizationId = procedure.organization.organization_id;
      const exactLocation = procedure.location?.location_id
        ? this.locations.find((location) => location.location_id === procedure.location.location_id)
        : null;

      if (exactLocation?.position) {
        this.nextLatLong = [exactLocation.position.latitude ?? 0, exactLocation.position.longitude ?? 0];
        resolve();
        return;
      }

      this.locationService.showByOrganization(organizationId).subscribe({
        next: (resOrganizationLocation) => {
          this.nextLatLong = [resOrganizationLocation.position.latitude ?? 0, resOrganizationLocation.position.longitude ?? 0];
          resolve();
        },
        error: (err) => {
          console.log(err);
          reject(err);
        },
      });
    });
  }

  getOrganizations() {
    this.organizationService.list().subscribe({
      next: (resOrganization: any) => {
        this.organizations = resOrganization;
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  getProcedures() {
    this.procedureService.getProcedures().subscribe((result: any[]) => {
      this.procedures = result.map(this.transformToFormData);
    });
  }

  getProceduresNearest() {
    this.procedures = [];
    this.loading = true;
    this.hasSearched = false;

    let params = new HttpParams();

    const position = this.latLong.join(',');
    params = params.set('position', position);
    params = params.set('radiusInMeters', this.distance);

    this.procedureService.listNearest(params).subscribe({
      next: (healthcareservices: Healthcareservice[]) => {
        this.procedures = healthcareservices;
        this.currentPage = 0;

        this.hasSearched = true;
        this.loading = false;
        if (this.search.trim().length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (error) => {
        this.loading = false;
        console.log(error);
      },
    });

    this.procedureService.listNearestBySpecialists(params).subscribe({
      next: (specialists) => {
        this.specialistResults = specialists;
        if (this.search.length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (error) => {
        this.specialistResults = [];
        console.error('Erro ao buscar especialistas próximos:', error);
      },
    });
  }

  getLocations() {
    this.locationService.list().subscribe({
      next: (resLocation: any) => {
        const res = resLocation;
        this.locations = res.map((location: any) => this.transformLocation(location));
        if (this.search.trim().length >= 2) this.updateAutocompleteSuggestions(this.search);
      },
      error: (err) => {
        console.log('erro', err);
      },
    });
  }

  getCity(organization_id: string) {
    const location = this.locations.find((location) => location.organization.organization_id == organization_id);
    if (!location) return null;
    return `${location?.city} / ${location.state}`;
  }

  getAddress(organization_id: string) {
    const location = this.locations.find((location) => location.organization.organization_id == organization_id);
    return location;
  }

  getAddressForProcedure(procedure: Healthcareservice) {
    const locationId = procedure.location?.location_id;
    if (locationId) {
      const exactLocation = this.locations.find((location) => location.location_id === locationId);
      if (exactLocation) return exactLocation;
    }
    return this.getAddress(procedure.organization.organization_id);
  }

  getClinicLocationLabel(procedure: Healthcareservice): string {
    const address = procedure.location?.address;
    const organizationAddress = this.getAddress(procedure.organization.organization_id);
    const city = address?.city || organizationAddress?.city;
    const state = address?.state || organizationAddress?.state;
    const cityLabel = city ? `${city}${state ? `, ${state}` : ''}` : '';
    const locationName =
      normalizeScheduleSearch(procedure.location?.name) === normalizeScheduleSearch(procedure.organization.name) ? '' : procedure.location?.name;
    return [locationName, cityLabel].filter(Boolean).join(' · ') || 'Localização não informada';
  }

  getProcedurePriceLabel(procedure: Healthcareservice): string {
    return summarizeSchedulePayment([procedure]).privatePriceLabel;
  }

  getProcedureCoverageLabel(procedure: Healthcareservice): string {
    return summarizeSchedulePayment([procedure]).coverageLabel;
  }

  getProcedureGroupPriceLabel(group: ProcedureCatalogGroup): string {
    return summarizeSchedulePayment(group.services).privatePriceLabel;
  }

  getProcedureGroupCoverageLabel(group: ProcedureCatalogGroup): string {
    return summarizeSchedulePayment(group.services).coverageLabel;
  }

  getClinicPriceLabel(group: ClinicCatalogGroup): string {
    return summarizeSchedulePayment(group.services).privatePriceLabel;
  }

  getClinicCoverageLabel(group: ClinicCatalogGroup): string {
    return summarizeSchedulePayment(group.services).coverageLabel;
  }

  getOrganizationPhoto(organization_id: string): string {
    const organization = this.organizations.find((org) => org.organization_id === organization_id);
    const url = organization && organization.photo ? organization.photo : this.defaultOrganizationPhoto;
    return url;
  }

  transformToFormData(healthcareserviceData: any): any {
    return {
      healthcareservice_id: healthcareserviceData.healthcareservice_id,
      name: healthcareserviceData.name,
      description: healthcareserviceData.description,
      organization: {
        name: healthcareserviceData.providedBy.display,
        organization_id: healthcareserviceData.providedBy.code,
      },
      extraDetails: healthcareserviceData.extraDetails,
      type: healthcareserviceData?.type[0]?.coding[0]?.display,
      price_low: parseInt(healthcareserviceData?.valueRange?.low?.value),
      price_high: parseInt(healthcareserviceData?.valueRange?.high?.value),
      status: healthcareserviceData.status,
      specialty: healthcareserviceData.specialty[0].coding[0].display,
      comment: healthcareserviceData.comments,
      appointmentRequired: healthcareserviceData.appointmentRequired,
    };
  }

  transformLocation(location: any) {
    const address = location?.address[0];
    const speciality = location?.type[0]?.coding[0];

    const newAddress: any = {};

    newAddress.location_id = location?.location_id;
    newAddress.position = location?.position;

    newAddress.organization = {
      name: location?.name,
      organization_id: location.organization_id,
    };

    const cep = address.postalCode;

    newAddress.cep = cep;

    newAddress.country = address?.country;

    newAddress.state = address?.state;

    newAddress.city = address?.city;

    newAddress.district = address?.district;
    const extraInfos = address?.line;

    if (extraInfos) {
      newAddress.street = extraInfos[0] ?? '';
      newAddress.number = extraInfos[1] ?? '';
      newAddress.complement = extraInfos[2] ?? '';
    }

    const contactInfo = this.extractContactInfo(location?.telecom);

    if (contactInfo) {
      newAddress.phone = contactInfo.phone ?? '';
      newAddress.mobile = contactInfo.mobile ?? '';
      newAddress.mail = contactInfo.email ?? '';
    }

    if (speciality) {
      newAddress.speciality = speciality?.display;
    }
    return newAddress;
  }

  extractContactInfo(telecom: telecom[]) {
    let email: string | null = null;
    let phone: string | null = null;
    let mobile: string | null = null;

    telecom.forEach((telecom) => {
      const { value, use, system } = telecom;
      if (system === 'email') {
        email = value;
      } else if (system === 'phone') {
        phone = value;
      } else if (system === 'mobile') {
        mobile = value;
      }
    });
    return { email, phone, mobile };
  }

  setPatientEncounterRaio() {
    const distanceInKm = localStorage.getItem('distancePatient') ?? '20000';

    this.distance = Number(distanceInKm);
  }

  formatLabel(value: number): string {
    localStorage.setItem('distancePatient', value.toString());

    if (value >= 1000) {
      return Math.round(value / 1000) + 'km';
    }

    return `${value}`;
  }

  ngOnDestroy() {
    localStorage.setItem('searchControl', this.search);
  }
}
